import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getFilteredRowModel,
  type ColumnDef,
  type FilterFn,
  type RowSelectionState,
  Row,
} from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { PathIndexed } from './constants';
import sortBy from 'lodash/sortBy';
import { get, isEqual } from 'lodash';
import { SHOW_ALL_ROWS, TABLE_MAX_ROWS } from 'src/app.constants';
import {
  getLocalJSON,
  matchesFilter,
  setLocalJSON,
} from 'src/utils/useful_functions';

type TTooltipFunc<T> = {
  (row: Row<T>): string;
};

type TOptions<T> = {
  grouping?: string[];
  getTooltip?: TTooltipFunc<T>;
  defaultPinnedColumn?: string;
  pageSize?: number;
  showSelectionColumn?: boolean;
  initialSort?: Array<{ id: string; desc: boolean }>;
  /** Used to persist intentionally-hidden custom field columns */
  tableId?: string;
};

function isCustomFieldColumnId(id: string | undefined | null): boolean {
  return typeof id === 'string' && id.startsWith('cf_');
}

export function hiddenCustomFieldsStorageKey(tableId: string) {
  return `lp.hiddenCustomFieldColumns.${tableId}`;
}

function getHiddenCustomFieldIds(tableId?: string): string[] {
  if (!tableId) return [];
  const hidden = getLocalJSON(hiddenCustomFieldsStorageKey(tableId), []);
  return Array.isArray(hidden) ? hidden : [];
}

function getWarning<T extends { warnings: any }>(row: Row<T>) {
  const hover = row.original?.warnings?.hover;
  if (hover) {
    return `Reason for error: ${hover}`;
  } else {
    return '';
  }
}

const DEFAULT_OPTIONS = {
  grouping: ['name'],
  getTooltip: getWarning,
};

/**
 * The text a cell actually shows, which is what a filter should match: numeric
 * columns convert metres to feet, coordinates are formatted, custom fields are
 * rendered from their definition. meta.csv is the same formatter the cell
 * renderer uses - it is what the CSV export writes - so it gives us the
 * displayed string without having to render React.
 */
function displayedValue(row: Row<any>, columnId: string) {
  const cell = row.getAllCells().find((c) => c.column.id === columnId);
  if (cell == null) {
    return row.getValue(columnId);
  }

  const format = cell.column.columnDef.meta?.csv;
  if (typeof format === 'function') {
    return format(cell.getContext());
  }

  return cell.getValue();
}

const regexFilterFn: FilterFn<any> = (row, columnId, filterValue) =>
  matchesFilter(displayedValue(row, columnId), filterValue);

function getColumnOrder(allColumnIds: string[], visibleColumns: string[]) {
  const originalOrderMap = new Map(
    allColumnIds.map((id, index) => [id, index])
  );

  const visibleColumnIds = allColumnIds.filter((id) =>
    visibleColumns.includes(id)
  );
  const nonVisibleColumnIds = allColumnIds.filter(
    (id) => !visibleColumns.includes(id)
  );

  const sortedVisibleColumnIds = sortBy(visibleColumnIds, (id) =>
    visibleColumns.indexOf(id)
  );
  const sortedNonVisibleColumnIds = sortBy(nonVisibleColumnIds, (id) =>
    originalOrderMap.get(id)
  );

  return [...sortedVisibleColumnIds, ...sortedNonVisibleColumnIds];
}

// Helper function to get range of rows between two selected rows
// see: https://github.com/TanStack/table/discussions/3068#discussioncomment-12041689
function getRowRange<TData>(
  rows: Array<Row<TData>>,
  clickedRowID: string,
  previousClickedRowID: string,
  getOriginalId: (row: Row<TData>) => string
) {
  const flatUIRows = rows.flatMap((row) => {
    const result = [row];
    if (row.subRows && row.subRows.length > 0) {
      // see GroupedRow.tsx
      result.push(...sortBy(row.subRows, (r: any) => r.original.path_index));
    }
    return result;
  });

  // Find indices of our target rows in the flattened array
  const clickedRowIndex = flatUIRows.findIndex(
    (row) => getOriginalId(row) === clickedRowID
  );
  const prevRowIndex = flatUIRows.findIndex(
    (row) => getOriginalId(row) === previousClickedRowID
  );

  // If we can't find one of the rows, just return the clicked row
  if (
    clickedRowIndex === -1 ||
    (prevRowIndex === -1 && previousClickedRowID !== '')
  ) {
    return [
      flatUIRows.find((row) => getOriginalId(row) === clickedRowID),
    ].filter(Boolean) as Array<Row<TData>>;
  }

  // If no previous row was selected, just return the clicked row
  if (previousClickedRowID === '') {
    return [flatUIRows[clickedRowIndex]];
  }

  // Determine start and end indices for the range
  const startIdx = Math.min(clickedRowIndex, prevRowIndex);
  const endIdx = Math.max(clickedRowIndex, prevRowIndex);

  // Return the slice of rows between start and end indices (inclusive)
  return flatUIRows.slice(startIdx, endIdx + 1);
}

export function useLpTable<T extends PathIndexed>({
  data,
  visibleColumns,
  columns,
  getRowId,
  options,
}: {
  data: T[];
  visibleColumns: string[];
  columns: ColumnDef<T>[];
  getRowId: (row: T) => string;
  options?: TOptions<T>;
}) {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  // Treated as the "anchor" row for range selection
  const [lastSelectedRowId, setLastSelectedRowId] = React.useState<
    string | null
  >(null);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: get(options, 'pageSize', TABLE_MAX_ROWS),
  });
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState({});

  const [columnOrder, setColumnOrder] = React.useState(
    columns.map((col) => col.id)
  );

  const defaultPinnedColumn = options?.defaultPinnedColumn ?? 'name';
  const tableId = options?.tableId;

  // The effect below rebuilds visibility and order from the API's visible list,
  // so it must only run when the set of columns actually changes. Keying it on
  // the ids rather than the array identity means a caller that recreates its
  // `columns` array each render cannot clobber the visibility the user has just
  // picked in Choose Columns.
  const columnIdsKey = columns.map((col) => col.id).join(',');
  const columnIds = React.useMemo(
    () => columns.map((col) => col.id as string),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnIdsKey]
  );

  useEffect(() => {
    const hiddenCustomFields = new Set(getHiddenCustomFieldIds(tableId));
    const customFieldIds = columnIds.filter(isCustomFieldColumnId);

    // Custom fields are visible by default. Respect Choose Columns hides
    // stored in localStorage, and include any cf_ columns from the API
    // visible list (or newly added ones not yet saved).
    const effectiveVisibleColumns = [
      ...visibleColumns.filter((id) => !hiddenCustomFields.has(id)),
      ...customFieldIds.filter(
        (id) =>
          !visibleColumns.includes(id) && !hiddenCustomFields.has(id)
      ),
    ];

    const nextVisibility = Object.fromEntries(
      columnIds.map((id) => [
        id,
        id === 'select' ||
          id === defaultPinnedColumn ||
          effectiveVisibleColumns.includes(id),
      ])
    );
    const nextOrder = getColumnOrder(columnIds, effectiveVisibleColumns);

    // Keep the previous state object when the result is unchanged. Both values
    // are rebuilt from scratch here, so storing them unconditionally would
    // re-render on every pass.
    setColumnVisibility((prev) =>
      isEqual(prev, nextVisibility) ? prev : nextVisibility
    );
    // Unfortunately, we don't have the visibleColumns initially since
    // they come from an API request, so we have to fix the order in an effect
    setColumnOrder((prev) => (isEqual(prev, nextOrder) ? prev : nextOrder));
  }, [visibleColumns, defaultPinnedColumn, columnIds, tableId]);

  const [isShowingAllRows, setIsShowingAllRows] = React.useState(false);
  const previousPageSizeRef = React.useRef(pagination.pageSize);
  const allowShowAllRows = data.length > previousPageSizeRef.current;

  useEffect(() => {
    if (!isShowingAllRows) {
      previousPageSizeRef.current = pagination.pageSize;
    }
  }, [isShowingAllRows, pagination.pageSize]);

  useEffect(() => {
    // Adjust pagination when data length is within threshold (aka fuzzy pagination)
    if (data.length > 0) {
      const threshold = TABLE_MAX_ROWS * 1.1; // 10% larger than TABLE_MAX_ROWS
      if (
        data.length > TABLE_MAX_ROWS &&
        data.length <= Math.floor(threshold)
      ) {
        setPagination((prev) => ({
          ...prev,
          pageSize: data.length,
        }));
      }
    }
  }, [data.length]);

  useEffect(() => {
    const shouldShowAllRows = getLocalJSON(SHOW_ALL_ROWS, false) === true;

    if (!shouldShowAllRows || data.length === 0) {
      return;
    }

    if (!isShowingAllRows) {
      previousPageSizeRef.current = pagination.pageSize;
      setIsShowingAllRows(true);
    }

    if (pagination.pageSize !== data.length) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        pageSize: data.length,
      }));
    }
  }, [data.length, isShowingAllRows, pagination.pageSize]);

  const toggleShowAllRows = () => {
    if (isShowingAllRows) {
      localStorage.removeItem(SHOW_ALL_ROWS);
      setPagination((prev) => ({
        ...prev,
        pageSize: previousPageSizeRef.current,
      }));
      setIsShowingAllRows(false);
    } else {
      previousPageSizeRef.current = pagination.pageSize;
      setLocalJSON(SHOW_ALL_ROWS, true);
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        pageSize: data.length,
      }));
      setIsShowingAllRows(true);
    }
  };

  // Handle row selection with shift and control key modifiers
  const handleRowSelectionWithShift = (
    rowId: string,
    shiftKey: boolean,
    ctrlKey = false
  ) => {
    // Case 1: No modifiers
    if (!shiftKey && !ctrlKey) {
      // Check if the row is already selected
      if (rowSelection[rowId]) {
        // If already selected, remove it from selection
        const newSelection = { ...rowSelection };
        delete newSelection[rowId];
        setRowSelection(newSelection);
      } else {
        // Otherwise, clear selection and select only this row
        setRowSelection({ [rowId]: true });
        setLastSelectedRowId(rowId);
      }
      return;
    }

    // Case 2: Control key (without shift) - toggle selection of the clicked row and set as anchor
    if (ctrlKey && !shiftKey) {
      const newSelection = { ...rowSelection };
      if (newSelection[rowId]) {
        delete newSelection[rowId];
      } else {
        newSelection[rowId] = true;
        setLastSelectedRowId(rowId);
      }
      setRowSelection(newSelection);
      return;
    }

    // Case 3: Shift key - select range from anchor to clicked row
    if (shiftKey) {
      // If no anchor exists yet, treat as single selection
      if (!lastSelectedRowId) {
        setRowSelection({ [rowId]: true });
        setLastSelectedRowId(rowId);
        return;
      }

      const { rows } = table.getSortedRowModel();
      const rowsToSelect = getRowRange(
        rows,
        rowId,
        lastSelectedRowId,
        (row) => {
          if (row.parentId == null) {
            return row.id;
          }
          return getRowId(row.original);
        }
      );

      // Create a new selection object
      // If control is also pressed, keep existing selection, otherwise start fresh
      const newSelection = ctrlKey ? { ...rowSelection } : {};

      // Add all rows in the range to the selection
      rowsToSelect.forEach((row) => {
        const originalId = getRowId(row.original);
        newSelection[originalId] = true;
      });

      setRowSelection(newSelection);
    }
  };

  if (options == null) {
    options = { ...DEFAULT_OPTIONS } as any;
  } else {
    options = { ...DEFAULT_OPTIONS, ...options } as any;
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
    enableSorting: true,
    initialState: {
      columnPinning: {
        left: ['select', defaultPinnedColumn],
      },
      sorting: options.initialSort || [
        { id: defaultPinnedColumn, desc: false },
      ],
      grouping: options.grouping,
      expanded: true,
    },
    state: {
      rowSelection,
      pagination,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
    },
    aggregationFns: {
      firstChild: (id, _, childRows) => {
        return childRows[0].getValue(id);
      },
    },
    // TanStack resolves a column with no filterFn of its own through 'auto',
    // which picks a filter from the type of the *first row's* value and never
    // consults options.filterFns. A column whose first row is null or a number
    // therefore silently got an equality filter, which is why the PTP Name
    // column returned nothing for "16". defaultColumn applies to every column
    // that does not declare its own filterFn; filterFns covers the ones that
    // ask for 'includesString' by name.
    defaultColumn: {
      filterFn: regexFilterFn,
    },
    filterFns: {
      includesString: regexFilterFn,
    },
    getRowId,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: regexFilterFn,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
  });

  return {
    table,
    getRowId,
    rowSelection,
    pagination,
    globalFilter,
    setGlobalFilter,
    columnOrder,
    setColumnOrder,
    isShowingAllRows,
    allowShowAllRows,
    toggleShowAllRows,
    setLastSelectedRowId,
    handleRowSelectionWithShift,
    getTooltip: options.getTooltip,
    defaultPinnedColumn,
    showSelectionColumn: options?.showSelectionColumn ?? true,
  };
}
